// @ts-expect-error: There is no type definition for react-https-redirect
import HttpsRedirect from "react-https-redirect";
import config from "config/config";
import Content from "content/content";
import Checklist from "features/checklist/Checklist";
import ConsentForm from "features/consent/ConsentForm";
import ConsentPage from "features/consent/ConsentPage";
import PassportPage from "features/document-page/components/passport/PassportPage";
import DocumentUpload from "features/document-upload/DocumentUpload";
import Ineligible from "features/ineligible/Ineligible";
import Layout from "features/layout/Layout";
import NotFound from "features/not-found/NotFound";
import DocumentProcessing from "features/document-processing/DocumentProcessing";
import QuestionsPage from "features/questions-page/QuestionsPage";
import SelectPassengerPage from "features/select-passenger/SelectPassengerPage";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { initScannerForType } from "services/document-capture/scanner-init";
import { useAppStore } from "store/store";
import { useShallow } from "zustand/shallow";
import DocumentCapture from "./document-capture/DocumentCapture";
import BasicDocumentPage from "./document-page/components/basic-document/BasicDocumentPage";
import InfoPage from "./document-page/components/info/InfoPage";
import ItemResolverWithOptions from "./document-page/components/item-resolver-with-options/ItemResolverWithOptions";
import ManualInputPage from "./document-page/components/manual-input/ManualInputPage";
import ErrorBoundary from "./ErrorBoundary";
import LoginForm from "./login/LoginForm";
import ScrollToContent from "./ScrollToContent";
import usePaxSession from "./shared/hooks/usePaxSession";
import ItemLevelPage from "features/item/ItemLevelPage";
import growthbook from "services/growthbook/growthbook";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import ScannerError from "models/scanner-error";
import AnalyticsService from "services/analytics/analytics";
import Outage from "./outage/Outage";
import AddressPage from "./address/AddressPage";

export default function App() {
  const locale = useAppStore((state) => state.locale);

  const direction = Content.shared.instance.dir();

  const [
    sessionId,
    areContentAssetsLoaded,
    markContentAssetsLoaded,
    setScannerError,
    markCapturerLoaded,
  ] = useAppStore(
    useShallow((state) => [
      state.sessionId,
      state.areContentAssetsLoaded,
      state.markContentAssetsLoaded,
      state.setScannerError,
      state.markCapturerLoaded,
    ])
  );

  const [isInitialized, setIsInitialized] = useState(false);

  const paxSessionId = usePaxSession()?.paxSessionId;
  const scannerIsInitializingRef = useRef(false);

  useEffect(() => {
    if (areContentAssetsLoaded) {
      return;
    }

    Content.shared.setup(locale, () => {
      markContentAssetsLoaded();
    });
  }, [areContentAssetsLoaded, locale, markContentAssetsLoaded]);

  useEffect(() => {
    if (isInitialized) return;

    if (!sessionId && !paxSessionId) {
      return;
    }

    onAppLoadFinished();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, sessionId, paxSessionId]);

  // set growthbook and analytics attributes when they become available
  useEffect(() => {
    if (!sessionId && !paxSessionId) {
      return;
    }
    if (sessionId) {
      AnalyticsService.shared.setSessionId(sessionId);
    }
    if (paxSessionId) {
      AnalyticsService.shared.setPaxSessionId(paxSessionId);
    }

    // growthbook
    growthbook.setAttributes({
      sessionId: sessionId,
      paxSessionId: paxSessionId,
    });
    growthbook.setTrackingCallback((experiment, result) => {
      AnalyticsService.shared.trackExperiment(experiment, result);
    });
  }, [sessionId, paxSessionId]);

  return (
    <HttpsRedirect>
      <GrowthBookProvider growthbook={growthbook}>
        <div className="app" dir={direction}>
          <BrowserRouter>
            <ErrorBoundary fallback={<NotFound />}>
              <Routes>
                <Route path="/ineligible" element={<Ineligible />} />
                <Route
                  path="/"
                  element={
                    <ScrollToContent>
                      <Layout />
                    </ScrollToContent>
                  }
                >
                  {!config.isIndexLoginDissalowed ? (
                    <Route path="login" element={<LoginForm />} />
                  ) : null}

                  <Route path="outage" element={<Outage />} />

                  <Route path="session/:sessionId" element={<Outlet />}>
                    <Route path="consent" element={<ConsentPage />}>
                      <Route path="" element={<ConsentForm />} />
                    </Route>

                    <Route
                      path="select-passenger"
                      element={<SelectPassengerPage />}
                    />

                    <Route path="pax/:paxSessionId" element={<Outlet />}>
                      <Route path="questions" element={<QuestionsPage />} />

                      <Route path="address" element={<AddressPage />} />

                      <Route path="checklist" element={<Checklist />} />

                      <Route path="item/:itemId" element={<ItemLevelPage />}>
                        <Route path="passport" element={<PassportPage />} />
                        <Route path="info" element={<InfoPage />} />
                        <Route
                          path="document"
                          element={<BasicDocumentPage />}
                        />
                        <Route
                          path="manual-input"
                          element={<ManualInputPage />}
                        />
                        <Route
                          path="item-resolver"
                          element={<ItemResolverWithOptions />}
                        />
                        <Route path="capture" element={<DocumentCapture />} />
                        <Route path="upload" element={<DocumentUpload />} />
                        <Route
                          path="processing"
                          element={<DocumentProcessing />}
                        />
                      </Route>

                      <Route
                        path="item-type/:itemType"
                        element={<ItemLevelPage />}
                      >
                        <Route path="passport" element={<PassportPage />} />
                        <Route
                          path="document"
                          element={<BasicDocumentPage />}
                        />

                        <Route
                          path="item-resolver"
                          element={<ItemResolverWithOptions />}
                        />
                        <Route path="capture" element={<DocumentCapture />} />
                        <Route path="upload" element={<DocumentUpload />} />
                        <Route
                          path="processing"
                          element={<DocumentProcessing />}
                        />
                      </Route>
                    </Route>
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </ErrorBoundary>
          </BrowserRouter>
        </div>
      </GrowthBookProvider>
    </HttpsRedirect>
  );


  function onAppLoadFinished() {
    setIsInitialized(true);
    initScanner();

    // initialize growthbook
    growthbook.loadFeatures();
  }

  function handleScannerInitError(e: ScannerError) {
    setScannerError(e);
  }

  function initScanner() {
    if (scannerIsInitializingRef.current) {
      return;
    }

    scannerIsInitializingRef.current = true;

    // Initialize the default scanner if it needs app-load init (only REGULA
    // does today). Scanners that self-initialize in their capturer (Capture,
    // BlinkID, Incode) are a no-op here.
    //
    // We deliberately init only the DEFAULT, not every enabled requiring-init
    // scanner: REGULA's init is heavy (loads the Regula engine/WASM from the
    // CDN), so initing it for everyone in a build where it's a minority A/B
    // variant would load that payload for users who never get Regula. The cost
    // of this choice is that REGULA only works as the default (see limitation #2
    // in document-capture/AGENTS.md); the proper fix for non-default REGULA is
    // to make it self-init in Capturer-REGULA on mount, like the others.
    void initScannerForType(
      config.defaultScanner,
      paxSessionId ? paxSessionId : sessionId || "missing",
      handleScannerInitError,
      markCapturerLoaded
    );
  }
}
